import { useState, useEffect, useRef } from 'react';
import { ChevronDown, CreditCard, Check, AlertCircle } from 'lucide-react';
import { useMediosPago } from '../../graphql/tesoreria';

/**
 * SelectorMedioPago - Select con búsqueda para medios de pago habilitados
 * Incluye lógica de validación según el tipo de medio
 * 
 * @param {Object} props
 * @param {string} props.value - ID del medio seleccionado
 * @param {Function} props.onChange - Callback al cambiar (recibe el objeto medio completo)
 * @param {string} props.label - Label del campo
 * @param {string} props.placeholder - Placeholder
 * @param {boolean} props.required - Si el campo es obligatorio
 * @param {string} props.error - Mensaje de error
 * @param {boolean} props.disabled - Deshabilitar el selector
 * @param {Function} props.onTipoChange - Callback cuando cambia el tipo (para mostrar advertencia de cuenta bancaria)
 */
export default function SelectorMedioPago({
  value,
  onChange,
  label = 'Medio de Pago',
  placeholder = 'Seleccionar medio',
  required = false,
  error = '',
  disabled = false,
  onTipoChange = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredMedios, setFilteredMedios] = useState([]);
  const containerRef = useRef(null);

  // TODO: Usar el hook real cuando esté conectado el backend
  const [result] = useMediosPago({ estado: true });
  const medios = result.data?.mediosPago?.data || [];

  const selectedMedio = medios.find((m) => m.id === value);

  useEffect(() => {
    if (search) {
      const filtered = medios.filter((medio) =>
        medio.nombre.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredMedios(filtered);
    } else {
      setFilteredMedios(medios);
    }
  }, [search, medios]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    // Notificar cambio de tipo si hay callback
    if (selectedMedio && onTipoChange) {
      onTipoChange(selectedMedio.tipo);
    }
  }, [selectedMedio, onTipoChange]);

  const handleSelect = (medio) => {
    onChange(medio);
    setIsOpen(false);
    setSearch('');
  };

  const getTipoIcon = (tipo) => {
    return <CreditCard className="w-4 h-4" aria-hidden="true" />;
  };

  const getTipoBadgeColor = (tipo) => {
    const colors = {
      EFECTIVO: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      TRANSFERENCIA: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      TARJETA: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      MONI: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      CHEQUE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      OTRO: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return colors[tipo] || colors.OTRO;
  };

  const requiereCuentaBancaria = (tipo) => {
    return ['TRANSFERENCIA', 'TARJETA', 'MONI'].includes(tipo);
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
          error
            ? 'border-red-500 focus:border-red-500'
            : 'border-[rgb(var(--border))] focus:border-[rgb(var(--brand))]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[rgb(var(--brand))]'}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={!!error}
      >
        {selectedMedio ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {getTipoIcon(selectedMedio.tipo)}
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <span className="font-medium text-[rgb(var(--fg))] truncate">
                {selectedMedio.nombre}
              </span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${getTipoBadgeColor(
                  selectedMedio.tipo
                )}`}
              >
                {selectedMedio.tipo}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-[rgb(var(--muted))]">{placeholder}</span>
        )}
        <ChevronDown
          className={`w-5 h-5 text-[rgb(var(--muted))] transition-transform flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}

      {/* Advertencia si el medio requiere cuenta bancaria y no tiene */}
      {selectedMedio &&
        requiereCuentaBancaria(selectedMedio.tipo) &&
        !selectedMedio.cuentaBancariaId && (
          <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Este medio de pago requiere asociación con una cuenta bancaria. Por favor,
              seleccione una cuenta.
            </p>
          </div>
        )}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-[rgb(var(--border))]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar medio de pago..."
              className="w-full px-3 py-2 rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] text-sm placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              aria-label="Buscar medio de pago"
              autoFocus
            />
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto" role="listbox">
            {filteredMedios.length > 0 ? (
              filteredMedios.map((medio) => (
                <button
                  key={medio.id}
                  type="button"
                  onClick={() => handleSelect(medio)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[rgba(var(--muted),0.3)] transition-colors ${
                    value === medio.id ? 'bg-[rgba(var(--brand),0.1)]' : ''
                  }`}
                  role="option"
                  aria-selected={value === medio.id}
                >
                  {getTipoIcon(medio.tipo)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[rgb(var(--fg))]">
                        {medio.nombre}
                      </span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${getTipoBadgeColor(
                          medio.tipo
                        )}`}
                      >
                        {medio.tipo}
                      </span>
                    </div>
                    {medio.cuentaBancaria && (
                      <div className="text-xs text-[rgb(var(--muted))] mt-0.5">
                        {medio.cuentaBancaria.banco} · {medio.cuentaBancaria.descripcion}
                      </div>
                    )}
                    {medio.notas && (
                      <div className="text-xs text-[rgb(var(--muted))] mt-0.5 truncate">
                        {medio.notas}
                      </div>
                    )}
                  </div>
                  {value === medio.id && (
                    <Check
                      className="w-5 h-5 text-[rgb(var(--brand))] flex-shrink-0"
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-[rgb(var(--muted))] text-sm">
                No se encontraron medios de pago
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
